import { Navigate, useParams, type RouteObject } from 'react-router-dom';

import { AppShellProviders } from '@/components/common/AppShellProviders';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';

import { EnvironmentsPanel } from './EnvironmentsPanel';
import { ReportProjectDetailPage } from './ReportProjectDetailPage';
import { ReportsBrowser } from './ReportsBrowser';
import { SubProjectsPage } from './SubProjectsPage';

/**
 * Reports feature routes (flat glob registry — `src/features/registry.ts`).
 *
 * Design §5.3: no `nav.ts` — sub-projects are reached through the project page
 * (IA: org → project → report-project), so the sidebar stays untouched. The
 * actual registry contract (verified in `registry.ts` + orgs/projects routes) is
 * a NAMED `routes: RouteObject[]` export from `routes.tsx`.
 *
 * The registry is flat and each protected feature owns its whole tree (the
 * orgs/projects coexistence precedent): React Router only accepts a branch that
 * matches the ENTIRE pathname, so the projects tree's `:projectId` child can
 * never consume `/reports/…` — this deeper `/…/reports/…` branch resolves here
 * and the two never collide. Because reports owns this subtree outright,
 * `ProjectDetailPage` needs NO `<Outlet/>` addition; the only host-side edit
 * left is embedding `SubProjectsSection` on the project page itself (reported
 * as the P3-T9 integration blocker — `SubProjectsPage` renders the same
 * section meanwhile so the whole flow is reachable via
 * `/organizations/:orgId/projects/:projectId/reports`).
 *
 * Tab routes mirror design §5.1/§5.2 and the orgs layout-route pattern: the
 * index redirects to `environments` (setup precedes data), tabs are URL-synced
 * Link-styled triggers rendered by `ReportProjectDetailPage` above its
 * `<Outlet/>`.
 */
export const routes: RouteObject[] = [
  {
    path: '/organizations/:orgId/projects/:projectId/reports',
    element: (
      <ProtectedRoute>
        <AppShellProviders>
          <AppLayout />
        </AppShellProviders>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <SubProjectsPage /> },
      {
        path: ':reportProjectId',
        element: <ReportProjectDetailPage />,
        children: [
          { index: true, element: <Navigate to="environments" replace /> },
          { path: 'environments', element: <EnvironmentsRoute /> },
          { path: 'reports', element: <ReportsRoute /> },
        ],
      },
    ],
  },
];

/** Route adapters: pull `reportProjectId` out of the params for the prop-driven panels. */
function EnvironmentsRoute() {
  const { reportProjectId = '' } = useParams<{ reportProjectId: string }>();
  return <EnvironmentsPanel reportProjectId={reportProjectId} />;
}

function ReportsRoute() {
  const { reportProjectId = '' } = useParams<{ reportProjectId: string }>();
  return <ReportsBrowser reportProjectId={reportProjectId} />;
}
